import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { hockeyNations, getNationByCode } from "@/data/hockeyNations";
import { Crown, Flag, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FavoriteNationSelectorProps {
  onOpenSubscription?: () => void;
  onSave?: () => void;
}

const FavoriteNationSelector = ({ onOpenSubscription, onSave }: FavoriteNationSelectorProps) => {
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const [selectedNation, setSelectedNation] = useState<string | null>(null);
  const [savedNation, setSavedNation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const canEdit = subscription.tier === "pro" || subscription.tier === "elite";

  useEffect(() => {
    const fetchFavoriteNation = async () => {
      if (!user) {
        setIsFetching(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("favorite_nation")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (data?.favorite_nation) {
          setSelectedNation(data.favorite_nation);
          setSavedNation(data.favorite_nation);
        }
      } catch (error) {
        console.error("Error fetching favorite nation:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchFavoriteNation();
  }, [user]);

  const handleSave = async () => {
    if (!user || !canEdit) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          favorite_nation: selectedNation,
          updated_at: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (error) throw error;

      setSavedNation(selectedNation);
      toast.success("Favorite nation saved!");
      onSave?.();
    } catch (error: any) {
      console.error("Error saving favorite nation:", error);
      toast.error("Failed to save favorite nation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (onOpenSubscription) {
      onOpenSubscription();
    } else {
      navigate("/pricing");
    }
  };

  const currentNation = getNationByCode(savedNation);
  const hasChanges = selectedNation !== savedNation;

  if (isFetching) {
    return (
      <Card className="bg-card/50 border-border">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Favorite National Team</CardTitle>
        </div>
        <CardDescription>
          Support your country in international tournaments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit ? (
          <>
            <Select
              value={selectedNation || ""}
              onValueChange={(value) => setSelectedNation(value === "__none__" ? null : (value || null))}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder="Select a national team">
                  {selectedNation && (
                    <span className="flex items-center gap-2">
                      <span>{getNationByCode(selectedNation)?.flag}</span>
                      <span>{getNationByCode(selectedNation)?.name}</span>
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {savedNation && (
                  <SelectItem value="__none__" className="text-muted-foreground italic">
                    <span className="flex items-center gap-2">
                      <span>✕</span>
                      <span>Remove favorite nation</span>
                    </span>
                  </SelectItem>
                )}
                {hockeyNations.map((nation) => (
                  <SelectItem key={nation.code} value={nation.code}>
                    <span className="flex items-center gap-2">
                      <span>{nation.flag}</span>
                      <span>{nation.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-md bg-muted/50">
              <div className="flex items-center gap-2">
                {currentNation ? (
                  <>
                    <span className="text-2xl">{currentNation.flag}</span>
                    <span className="font-medium">{currentNation.name}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">None selected</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-md bg-primary/10 border border-primary/20">
              <Crown className="h-4 w-4 text-primary flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro or Elite to set your favorite nation
              </p>
            </div>

            <Button onClick={handleUpgrade} variant="outline" className="w-full">
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to Pro
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FavoriteNationSelector;
